import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getPaymentStatus } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { AibaLogo } from "../components/site/Logo";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) return setState("error");
      if (attempt >= MAX_POLLS) return setState("pending");
      try {
        const { data } = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          refresh();
          return setState("paid");
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  const config = {
    checking: { icon: <Loader2 size={44} className="animate-spin text-blue-400" />, title: "Vérification du protocole...", sub: "Nous confirmons votre transaction chiffrée." },
    paid: { icon: <CheckCircle2 size={44} className="text-blue-400" />, title: "Paiement confirmé", sub: "Votre accès est désormais actif. Bienvenue dans le cockpit AIBA." },
    pending: { icon: <Loader2 size={44} className="animate-spin text-blue-400" />, title: "Traitement en cours", sub: "Le paiement est en cours de confirmation. Vous recevrez un e-mail sous peu." },
    error: { icon: <XCircle size={44} className="text-red-400" />, title: "Transaction non confirmée", sub: "Aucun paiement n'a été finalisé. Vous pouvez réessayer depuis les tarifs." },
  }[state];

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#0F172A] px-6" data-testid="payment-success-page">
      <div className="grid-lines absolute inset-0 opacity-50" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(37,99,235,0.12),transparent_60%)]" />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-blue-500/20 bg-blue-400/[0.03] p-10 text-center backdrop-blur-xl glow-cyan">
        <div className="mx-auto mb-6 flex justify-center"><AibaLogo size={38} /></div>
        <div className="mb-6 flex justify-center" data-testid="payment-status-icon">{config.icon}</div>
        <h1 className="font-display text-2xl font-800 tracking-tighter text-white" data-testid="payment-status-title">{config.title}</h1>
        <p className="mt-3 font-body text-sm leading-relaxed text-[#8AA0C0]">{config.sub}</p>
        <button
          data-testid="payment-back-home"
          onClick={() => navigate("/")}
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-400/5 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-blue-100 transition-all duration-300 hover:bg-blue-400 hover:text-[#0F172A]"
        >
          <ArrowLeft size={14} /> Retour à l'accueil
        </button>
      </div>
    </div>
  );
}
